import { ProgressKind, RequestKind, boolean, string } from '@sb/library/base';
import { AutoInvoiceSummaryEmailInputItem, BatchResultItem, ColumnMatchInputItem, DisplayItemCollection, IImportErrorResponse, TeacherAttachmentDisplayItem, TeacherAttachmentInputItem, TeacherAttachmentSearchOptions, TeacherAttachmentUpdateItem, TeacherColumnMatchesDisplayItem, TeacherContactDisplayItem, TeacherContactSearchOptions, TeacherDisplayItem, TeacherInputItem, TeacherNameDisplayItem, TeacherNameSearchOptions, TeacherProfileDisplayItem, TeacherProfileSearchOptions, TeacherProfileUpdateItem, TeacherSearchOptions, TeacherSummaryDisplayItem, TeacherSummarySearchOptions, TeacherUpdateItem, TeachersImportInputItem } from '@sb/library/data-models';
import { ApiRoute } from '../api-route';

export namespace TeacherRoutes {
    export const Add = ApiRoute.Define({
        URL: 'teachers',
        Kind: RequestKind.Post,
        BodyType: TeacherInputItem,
        ResultType: string
    });

    export const Update = ApiRoute.Define({
        URL: (id: string) => `teachers/${id}`,
        Kind: RequestKind.Put,
        BodyType: TeacherUpdateItem,
        ResultType: boolean
    });

    export const Delete = ApiRoute.Define({
        URL: (id: string) => `teachers/${id}`,
        Kind: RequestKind.Delete,
        ResultType: boolean
    });

    export const Get = ApiRoute.Define({
        URL: (id: string) => `teachers/${id}`,
        Kind: RequestKind.Get,
        ResultType: TeacherDisplayItem
    });

    export const GetAll = ApiRoute.Define({
        URL: 'teachers',
        Kind: RequestKind.Get,
        ResultType: DisplayItemCollection<TeacherDisplayItem>
    });

    export const GetNames = ApiRoute.Define({
        URL: 'names/teachers',
        Kind: RequestKind.Get,
        ResultType: Array<TeacherNameDisplayItem>,
        OrderByType: TeacherNameDisplayItem
    });

    export const SearchNames = ApiRoute.Define({
        URL: 'search/names/teachers',
        Kind: RequestKind.Post,
        BodyType: TeacherNameSearchOptions,
        ResultType: Array<TeacherNameDisplayItem>,
        OrderByType: TeacherNameDisplayItem
    });

    export const Search = ApiRoute.Define({
        URL: 'search/teachers',
        Kind: RequestKind.Post,
        BodyType: TeacherSearchOptions,
        ResultType: DisplayItemCollection<TeacherDisplayItem>,
        PagingAllowed: true
    });

    export const SendAutoInvoiceSummary = ApiRoute.Define({
        URL: (teacherID: string) => `teachers/${teacherID}/sendautoinvoicesummary`,
        Kind: RequestKind.Post,
        BodyType: AutoInvoiceSummaryEmailInputItem,
        ResultType: boolean
    });

    export const SendDailyAgenda = ApiRoute.Define({
        URL: (id: string) => `teachers/${id}/senddailyagenda`,
        Kind: RequestKind.Post,
        ResultType: boolean
    });

    export namespace Profile {
        export const Get = ApiRoute.Define({
            URL: (teacherID: string) => `teacherprofiles/${teacherID}`,
            Kind: RequestKind.Get,
            ResultType: TeacherProfileDisplayItem
        });

        export const GetAll = ApiRoute.Define({
            URL: 'teacherprofiles',
            Kind: RequestKind.Get,
            ResultType: DisplayItemCollection<TeacherProfileDisplayItem>
        });

        export const Search = ApiRoute.Define({
            URL: 'search/teacherprofiles',
            Kind: RequestKind.Post,
            BodyType: TeacherProfileSearchOptions,
            ResultType: DisplayItemCollection<TeacherProfileDisplayItem>,
            PagingAllowed: true
        });

        export const Update = ApiRoute.Define({
            URL: (id: string) => `teacherprofiles/${id}`,
            Kind: RequestKind.Put,
            BodyType: TeacherProfileUpdateItem,
            ResultType: boolean
        });

        export namespace Picture {
            export const Update = ApiRoute.Define({
                URL: (id: string) => `teachers/${id}/image`,
                Kind: RequestKind.Put,
                BodyType: FormData,
                ResultType: string    // Should be boolean, but this is because of stupid typing in the upload component
            });

            export const Delete = ApiRoute.Define({
                URL: (id: string) => `teachers/${id}/image`,
                Kind: RequestKind.Delete,
                ResultType: boolean
            });

            export const Preview = ApiRoute.Define({
                URL: 'teachers/imagepreview',
                Kind: RequestKind.Post,
                BodyType: FormData,
                ResultType: string
            });
        }
    }

    export namespace Contact {
        export const Get = ApiRoute.Define({
            URL: (id: string) => `teachercontacts/${id}`,
            Kind: RequestKind.Get,
            ResultType: TeacherContactDisplayItem
        });

        export const GetAll = ApiRoute.Define({
            URL: 'teachercontacts',
            Kind: RequestKind.Get,
            ResultType: DisplayItemCollection<TeacherContactDisplayItem>
        });

        export const Search = ApiRoute.Define({
            URL: 'search/teachercontacts',
            Kind: RequestKind.Post,
            BodyType: TeacherContactSearchOptions,
            ResultType: DisplayItemCollection<TeacherContactDisplayItem>,
            PagingAllowed: true
        });
    }

    export namespace Attachments {
        export const Get = ApiRoute.Define({
            URL: (id: string) => `teacherattachments/${id}`,
            Kind: RequestKind.Get,
            ResultType: TeacherAttachmentDisplayItem
        });

        export const GetAll = ApiRoute.Define({
            URL: 'teacherattachments',
            Kind: RequestKind.Get,
            ResultType: DisplayItemCollection<TeacherAttachmentDisplayItem>
        });

        export const Search = ApiRoute.Define({
            URL: 'search/teacherattachments',
            Kind: RequestKind.Post,
            BodyType: TeacherAttachmentSearchOptions,
            ResultType: DisplayItemCollection<TeacherAttachmentDisplayItem>
        });

        export const Add = ApiRoute.Define({
            URL: 'teacherattachments',
            Kind: RequestKind.Post,
            BodyType: TeacherAttachmentInputItem,
            ResultType: string
        });

        export const Update = ApiRoute.Define({
            URL: (id: string) => `teacherattachments/${id}`,
            Kind: RequestKind.Put,
            BodyType: TeacherAttachmentUpdateItem,
            ResultType: boolean
        });

        export const Delete = ApiRoute.Define({
            URL: (id: string) => `teacherattachments/${id}`,
            Kind: RequestKind.Delete,
            ResultType: boolean
        });
    }

    export const Summary = ApiRoute.Define({
        URL: 'summary/teachers',
        Kind: RequestKind.Post,
        BodyType: TeacherSummarySearchOptions,
        ResultType: Array<TeacherSummaryDisplayItem>
    });

    export namespace Import {
        export const MatchColumns = ApiRoute.Define({
            URL: 'teachers/import/matchcolumns',
            Kind: RequestKind.Post,
            BodyType: Array<ColumnMatchInputItem>,
            ResultType: TeacherColumnMatchesDisplayItem
        });

        export const Import = ApiRoute.Define({
            URL: 'teachers/import',
            Kind: RequestKind.Post,
            BodyType: TeachersImportInputItem,
            ResultType: Array<BatchResultItem<boolean, IImportErrorResponse>>,
            ExtraParameters: 'dryRun',
            ShowProgress: ProgressKind.Computation
        });
    }
}
